// src/pages/08_admin/AdminDashboard.js
import React, { useEffect, useState, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { fetchAdminUsers, updateUserActive, updateUserRole, assignStudentToTeacher } from "../../features/admin/adminSlice";
import {
    Wrapper,
    Section,
    Title,
    TableWrapper,
    ControlsRow,
    FiltersGroup,
    SummaryGroup,
    FilterSelect,
    FilterInput,
    UsersTable,
    UsersHeadCell,
    UsersRow,
    UsersCell,
    RoleBadge,
    ActiveBadge,
    ActionButton,
} from "./AdminDashboardStyle";

const AdminDashboard = () => {
    const dispatch = useDispatch();
    const { users, loading, error } = useSelector((state) => state.admin);

    // 🔍 Filter state
    const [roleFilter, setRoleFilter] = useState("");
    const [statusFilter, setStatusFilter] = useState("");
    const [searchText, setSearchText] = useState("");
    const [teacherFilter, setTeacherFilter] = useState("");

    const handleRoleChange = (userId, newRole, currentRole) => {
        if (newRole === currentRole) return;
        dispatch(updateUserRole({ userId, role: newRole }));
    };

    const handleAssignTeacher = (studentId, teacherIdValue) => {
        if (!teacherIdValue) {
            // For now, we won't support "unassign" via dropdown (empty value).
            // You could add a separate endpoint later if you want to clear teacherId.
            return;
        }

        const teacherId = Number(teacherIdValue);
        if (!Number.isInteger(teacherId)) return;

        dispatch(assignStudentToTeacher({ studentId, teacherId }));
    };

    const teachers = useMemo(
        () => users.filter((u) => u.role === "TEACHER" && u.isActive),
        [users]
    );

    useEffect(() => {
        dispatch(fetchAdminUsers());
    }, [dispatch]);

    // Apply filters in memory
    const filteredUsers = useMemo(() => {
        return users.filter((u) => {
            if (roleFilter && u.role !== roleFilter) return false;

            if (statusFilter === "active" && !u.isActive) return false;
            if (statusFilter === "inactive" && u.isActive) return false;

            // NEW: filter by teacher ⇒ show only that teacher's students
            if (teacherFilter) {
                if (teacherFilter === "unassigned") {
                    // ONLY students without teacherId
                    if (!(u.role === "STUDENT" && !u.teacherId)) return false;
                } else {
                    // teacherFilter is a real teacherId (string)
                    const tid = Number(teacherFilter);
                    if (!(u.role === "STUDENT" && u.teacherId === tid)) return false;
                }
            }

            if (searchText.trim()) {
                const q = searchText.trim().toLowerCase();
                const haystack = `${u.email || ""} ${u.name || ""}`.toLowerCase();
                if (!haystack.includes(q)) return false;
            }

            return true;
        });
    }, [users, roleFilter, statusFilter, searchText, teacherFilter]);

    const totalUsers = users.length;
    const totalStudents = users.filter((u) => u.role === "STUDENT").length;
    const totalTeachers = users.filter((u) => u.role === "TEACHER").length;
    const totalAdmins = users.filter((u) => u.role === "ADMIN").length;

    return (
        <Wrapper>
            <Section>
                <Title>Admin Console</Title>

                <TableWrapper>
                    <ControlsRow>
                        <FiltersGroup>
                            <FilterSelect
                                value={roleFilter}
                                onChange={(e) => setRoleFilter(e.target.value)}
                            >
                                <option value="">All roles</option>
                                <option value="STUDENT">Students</option>
                                <option value="TEACHER">Teachers</option>
                                <option value="ADMIN">Admins</option>
                            </FilterSelect>

                            <FilterSelect
                                value={statusFilter}
                                onChange={(e) => setStatusFilter(e.target.value)}
                            >
                                <option value="">All statuses</option>
                                <option value="active">Active</option>
                                <option value="inactive">Inactive</option>
                            </FilterSelect>

                            <FilterInput
                                type="text"
                                placeholder="Search by email or name"
                                value={searchText}
                                onChange={(e) => setSearchText(e.target.value)}
                            />

                            <span>Show students for:</span>
                            <FilterSelect
                                value={teacherFilter}
                                onChange={(e) => setTeacherFilter(e.target.value)}
                            >
                                <option value="">All teachers</option>
                                {teachers.map((t) => (
                                    <option key={t.id} value={t.id}>
                                        {t.name || t.email} (id: {t.id})
                                    </option>
                                ))}
                                <option value="unassigned">Unassigned</option>
                            </FilterSelect>

                        </FiltersGroup>

                        <SummaryGroup>
                            <div>Total: {totalUsers}</div>
                            <div>
                                S: {totalStudents} | T: {totalTeachers} | A: {totalAdmins}
                            </div>
                            <div>Showing: {filteredUsers.length}</div>
                        </SummaryGroup>
                    </ControlsRow>

                    {loading && <p>Loading users...</p>}
                    {error && <p style={{ color: "red" }}>Error: {error}</p>}

                    {!loading && !error && filteredUsers.length === 0 && (
                        <p>No users match selected filters.</p>
                    )}

                    {!loading && !error && filteredUsers.length > 0 && (
                        <UsersTable>
                            <thead>
                                <tr>
                                    <UsersHeadCell>ID</UsersHeadCell>
                                    <UsersHeadCell>Email</UsersHeadCell>
                                    <UsersHeadCell>Name</UsersHeadCell>
                                    <UsersHeadCell>Role</UsersHeadCell>
                                    <UsersHeadCell>Active</UsersHeadCell>
                                    <UsersHeadCell>Teacher ID</UsersHeadCell>
                                </tr>
                            </thead>
                            <tbody>
                                {filteredUsers.map((u) => (
                                    <UsersRow key={u.id}>
                                        <UsersCell data-label="ID">{u.id}</UsersCell>
                                        <UsersCell data-label="Email">{u.email}</UsersCell>
                                        <UsersCell data-label="Name">{u.name}</UsersCell>

                                        <UsersCell data-label="Role">
                                            <br />
                                            <FilterSelect
                                                value={u.role}
                                                onChange={(e) => handleRoleChange(u.id, e.target.value, u.role)}
                                                style={{ marginTop: "4px" }}
                                            >
                                                <option value="STUDENT">STUDENT</option>
                                                <option value="TEACHER">TEACHER</option>
                                                <option value="ADMIN">ADMIN</option>
                                            </FilterSelect>
                                        </UsersCell>

                                        <UsersCell data-label="Active">
                                            <ActiveBadge
                                                active={u.isActive}
                                                clickable
                                                title="Click to toggle active status"
                                                onClick={() =>
                                                    dispatch(updateUserActive({ userId: u.id, isActive: !u.isActive }))
                                                }
                                            >
                                                {u.isActive ? "Active" : "Inactive"}
                                            </ActiveBadge>
                                        </UsersCell>

                                        <UsersCell data-label="Teacher">
                                            {u.role === "STUDENT" ? (
                                                <FilterSelect
                                                    value={u.teacherId || ""}
                                                    onChange={(e) => handleAssignTeacher(u.id, e.target.value)}
                                                >
                                                    <option value="">
                                                        {u.teacherId ? "Change teacher..." : "Assign teacher..."}
                                                    </option>
                                                    {teachers.map((t) => (
                                                        <option key={t.id} value={t.id}>
                                                            {t.name || t.email} (id: {t.id})
                                                        </option>
                                                    ))}
                                                </FilterSelect>
                                            ) : (
                                                <span>—</span>
                                            )}
                                        </UsersCell>
                                    </UsersRow>

                                ))}
                            </tbody>
                        </UsersTable>
                    )}
                </TableWrapper>
            </Section>
        </Wrapper>
    );
};

export default AdminDashboard;
